import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import { fetchTicketsByPlates, updatePlateInDataBase } from '../Functions';
import { addPlateToClient, updatePlateFromClient } from '../ReduxSlices/clientsSlice';
import { setEditingPlate } from '../ReduxSlices/platesSlice';
import {
  Client,
  PlateClassification,
  DropdownItem,
  MonitoringList,
  Plate,
  WEFResponse
} from '../Types';
import CustomDatePicker from './FormComponents/CustomDatePicker';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useMonitoringListQuery } from '../queries/monitoring.query';
import { customAxiosFetch } from '../helpers/customFetch';
import Select from 'react-select';
import toast from 'react-hot-toast';
import {
  FaCar,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaEye,
  FaPlus,
  FaTimes,
  FaSave
} from 'react-icons/fa';

const initialNewPlateState = {
  open: false,
  plateNumber: '',
  monitoringThisPlate: false,
  startDate: '',
  endDate: '',
  state: { label: 'New York', value: 'NY' },
  monitoringRangeId: '',
  registrationExpirationDate: '',
  registrationAddress: '',
  classification: PlateClassification.Unassigned
};

const classificationOptions: DropdownItem<number>[] = [
  { value: PlateClassification.Unassigned, label: 'Unassigned' },
  { value: PlateClassification.Personal, label: 'Personal' },
  { value: PlateClassification.Business, label: 'Business' }
];

const states = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'District of Columbia', value: 'DC' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Guam', value: 'GU' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Northern Mariana Islands', value: 'MP' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Trust Territories', value: 'TT' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Virgin Islands', value: 'VI' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' }
];

const returnFormattedDateString = (dateInstance: Date) => dateInstance.toISOString().slice(0, 10);
const returnFormattedDateStringV2 = (dateString: string) => {
  return dateString.split('T')[0];
};
const currentDate = new Date();
const oneMonthPrior = new Date(currentDate);
oneMonthPrior.setMonth(currentDate.getMonth() - 1);
const maxDate = '2999-01-01';

const plateReducer = (state, action) => {
  const plate = action.payload;
  switch (action.type) {
    case 'toggleOpen':
      return state.open ? initialNewPlateState : { ...state, open: !state.open };
    case 'stagePlateEdit': {
      let selectedState = states.find((s) => s.value === plate.state);
      if (selectedState === undefined) {
        selectedState = null;
      }

      return {
        ...initialNewPlateState,
        plateNumber: plate.plateNumber,
        monitoringThisPlate: !!plate.monitoringRange,
        startDate: plate.monitoringRange
          ? returnFormattedDateStringV2(plate.monitoringRange.startDate)
          : '',
        endDate: plate.monitoringRange
          ? returnFormattedDateStringV2(plate.monitoringRange.endDate)
          : '',
        state: selectedState,
        monitoringRangeId: plate.monitoringRangeId,
        registrationExpirationDate: plate.registrationExpirationDate
          ? returnFormattedDateStringV2(plate.registrationExpirationDate)
          : '',
        registrationAddress: plate.registrationAddress || '',
        classification: plate.classification ?? PlateClassification.Unassigned
      };
    }
    case 'setPlateNumber':
      return { ...state, plateNumber: action.payload };
    case 'setState':
      return { ...state, state: action.payload };
    case 'toggleMonitoringThisPlate': {
      return {
        ...state,
        monitoringThisPlate: !state.monitoringThisPlate,
        startDate: !state.monitoringThisPlate ? returnFormattedDateString(oneMonthPrior) : '',
        endDate: !state.monitoringThisPlate ? maxDate : ''
      };
    }
    case 'setStartDate':
      return { ...state, startDate: action.payload };
    case 'setEndDate':
      return { ...state, endDate: action.payload };
    case 'setRegistrationExpirationDate':
      return { ...state, registrationExpirationDate: action.payload };
    case 'setRegistrationAddress':
      return { ...state, registrationAddress: action.payload };
    case 'setClassification':
      return { ...state, classification: action.payload };
    case 'reset':
      return { ...initialNewPlateState };
  }
};

interface INewPlateCollapseProps {
  delinquent?: string;
  client: Client;
  monitoringList: MonitoringList;
  disabled?: boolean;
}
function NewPlateCollapse(props: INewPlateCollapseProps) {
  const { monitoringList, client, disabled } = props;
  const [chargeCC, setChargeCC] = useState(false);
  const [newPlateState, dispatch] = useReducer(plateReducer, initialNewPlateState);
  const reduxDispatch = useAppDispatch();
  const { data } = useMonitoringListQuery({ enabled: true });
  const { unassignedMonitoringItem } = data ?? {};
  const editingPlate = useAppSelector((state) => state.plates.editingPlate);

  const createNewPlate = async () => {
    const callback = (result: WEFResponse<Plate>) => {
      const { data } = result;
      reduxDispatch(addPlateToClient(data));
      dispatch({ type: 'toggleOpen' });
      fetchTicketsByPlates([newPlateState.plateNumber], {
        clientId: data.clientId,
        state: newPlateState.state?.value
      });
    };
    await customAxiosFetch({
      endpoint: '/Plate',
      callback: callback,
      config: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        data: JSON.stringify({
          plateNumber: newPlateState.plateNumber,
          clientId: client.id,
          state: newPlateState.state?.value,
          monitoringStartDate: newPlateState.startDate,
          monitoringEndDate: newPlateState.endDate,
          registrationExpirationDate: newPlateState.registrationExpirationDate || null,
          registrationAddress: newPlateState.registrationAddress || null,
          classification: newPlateState.classification
        })
      }
    });
  };

  const updatePlate = () => {
    updatePlateInDataBase(
      {
        ...editingPlate,
        plateNumber: newPlateState.plateNumber,
        state: newPlateState.state?.value,
        monitoringStartDate: newPlateState.startDate,
        monitoringEndDate: newPlateState.endDate,
        monitoringRangeId: newPlateState.monitoringThisPlate
          ? newPlateState.monitoringRangeId
          : null,
        registrationExpirationDate: newPlateState.registrationExpirationDate || null,
        registrationAddress: newPlateState.registrationAddress || null,
        classification: newPlateState.classification
      },
      (modifiedPlate: Plate) => {
        reduxDispatch(updatePlateFromClient(modifiedPlate));
        handleResetPlateState();
      }
    );
  };

  const handleFormSubmit = (e?: any) => {
    e?.preventDefault();
    if (newPlateState.startDate > newPlateState.endDate) {
      toast.error('Monitoring start date must be less than the monitoring end date');
      return;
    }
    if (editingPlate) {
      updatePlate();
    } else {
      createNewPlate();
    }
  };

  useEffect(() => {
    if (editingPlate) {
      dispatch({ type: 'stagePlateEdit', payload: editingPlate });
    }
  }, [editingPlate]);
  useEffect(() => {
    dispatch({ type: 'reset' });
  }, []);

  const handleStateChange = (value) => {
    dispatch({ type: 'setState', payload: value });
  };

  const handleResetPlateState = () => {
    reduxDispatch(setEditingPlate(null));
    dispatch({ type: 'reset' });
  };

  const handlePlateNumberChange = (e) => {
    const regex = /^[^\s]+$/;
    const value = e.target.value;

    if (value === '' || regex.test(value)) {
      dispatch({ type: 'setPlateNumber', payload: e.target.value });
    }
  };

  const disabledSavePlateButton = useMemo(() => {
    return (
      !newPlateState.plateNumber ||
      (newPlateState.monitoringThisPlate && !client.recurringInvoiceFreshbooksId && !chargeCC)
    );
  }, [
    chargeCC,
    client.recurringInvoiceFreshbooksId,
    newPlateState.monitoringThisPlate,
    newPlateState.plateNumber
  ]);

  return disabled ? null : (
    <>
      {!newPlateState.open && !editingPlate && (
        <Button
          variant="primary"
          onClick={() => dispatch({ type: 'toggleOpen' })}
          className="d-flex align-items-center gap-2">
          <FaPlus /> New Plate
        </Button>
      )}

      <Collapse in={newPlateState.open || !!editingPlate}>
        <div>
          <Card className="shadow-sm border-0 mt-3">
            <Card.Header
              className="d-flex align-items-center gap-2"
              style={{ backgroundColor: '#1a365d', color: 'white' }}>
              <FaCar size={20} />
              <strong>{editingPlate ? 'Edit Plate' : 'Add New Plate'}</strong>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleFormSubmit}>
                {/* Plate Preview */}
                {newPlateState.plateNumber && (
                  <div className="d-flex justify-content-center mb-4">
                    <div
                      style={{
                        background: 'linear-gradient(180deg, #FFD700 0%, #FFC107 100%)',
                        border: '3px solid #1a365d',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        minWidth: '200px',
                        textAlign: 'center',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                      <div
                        style={{
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: '#1a365d',
                          letterSpacing: '2px'
                        }}>
                        {newPlateState.state?.label?.toUpperCase() || 'NEW YORK'}
                      </div>
                      <div
                        style={{
                          fontFamily: 'FE-Schrift, monospace',
                          fontSize: '32px',
                          fontWeight: 'bold',
                          color: '#1a365d',
                          letterSpacing: '3px'
                        }}>
                        {newPlateState.plateNumber.toUpperCase()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Plate Information Section */}
                <div className="mb-4">
                  <h6 className="text-muted mb-3 d-flex align-items-center gap-2">
                    <FaCar /> Plate Information
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Plate Number *</Form.Label>
                        <Form.Control
                          autoFocus
                          placeholder="Enter plate number"
                          value={newPlateState.plateNumber}
                          onChange={handlePlateNumberChange}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">State</Form.Label>
                        <Select
                          options={states}
                          placeholder="Select state"
                          isClearable={true}
                          onChange={handleStateChange}
                          value={newPlateState.state}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Classification</Form.Label>
                        <Select
                          options={classificationOptions}
                          placeholder="Select classification"
                          onChange={(option) =>
                            dispatch({
                              type: 'setClassification',
                              payload: option?.value ?? PlateClassification.Unassigned
                            })
                          }
                          value={classificationOptions.find(
                            (opt) => opt.value === newPlateState.classification
                          )}
                        />
                        <Form.Text className="text-muted">
                          Business (B), Personal (P), or Unassigned (U)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Registration Information Section */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <h6 className="text-muted mb-3 d-flex align-items-center gap-2">
                    <FaCalendarAlt /> Registration Information
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                          <FaCalendarAlt className="text-primary" /> Registration Expiration
                        </Form.Label>
                        <CustomDatePicker
                          onChange={(e) =>
                            dispatch({
                              type: 'setRegistrationExpirationDate',
                              payload: e.target.value
                            })
                          }
                          value={newPlateState.registrationExpirationDate}
                        />
                        <Form.Text className="text-muted">
                          When does the vehicle registration expire?
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                          <FaMapMarkerAlt className="text-danger" /> Registration Address
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter registered address"
                          value={newPlateState.registrationAddress}
                          onChange={(e) =>
                            dispatch({
                              type: 'setRegistrationAddress',
                              payload: e.target.value
                            })
                          }
                        />
                        <Form.Text className="text-muted">
                          Address on the vehicle registration
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Monitoring Section */}
                {monitoringList && monitoringList?.id !== unassignedMonitoringItem?.id && (
                  <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#e8f4fd' }}>
                    <h6 className="text-muted mb-3 d-flex align-items-center gap-2">
                      <FaEye /> Monitoring Settings
                    </h6>
                    <Form.Group className="mb-3">
                      <Form.Check
                        label={
                          <span className="fw-semibold">Enable Monitoring for this Plate</span>
                        }
                        type="switch"
                        checked={newPlateState.monitoringThisPlate}
                        onChange={() => dispatch({ type: 'toggleMonitoringThisPlate' })}
                      />
                    </Form.Group>
                    {newPlateState.monitoringThisPlate && (
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Monitoring Start Date</Form.Label>
                            <CustomDatePicker
                              onChange={(e) =>
                                dispatch({
                                  type: 'setStartDate',
                                  payload: e.target.value
                                })
                              }
                              value={newPlateState.startDate}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Monitoring End Date</Form.Label>
                            <CustomDatePicker
                              onChange={(e) =>
                                dispatch({
                                  type: 'setEndDate',
                                  payload: e.target.value
                                })
                              }
                              value={newPlateState.endDate}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                  </div>
                )}

                {newPlateState.monitoringThisPlate && !client.recurringInvoiceFreshbooksId && (
                  <Alert variant="warning" className="d-flex align-items-start gap-2">
                    <div>
                      <p className="fw-bold mb-1">Warning!</p>
                      <p className="mb-2">
                        This client does not yet have a recurring invoice in our system. If they do
                        not have a recurring invoice set up in freshbooks,{' '}
                        <strong>${monitoringList?.amount}</strong> will be charged to their card.
                      </p>
                      <Form.Check
                        label={
                          <span>
                            Charge <strong>${monitoringList?.amount}</strong> to Client&apos;s
                            Credit Card
                          </span>
                        }
                        type="switch"
                        checked={chargeCC}
                        onChange={() => setChargeCC(!chargeCC)}
                      />
                    </div>
                  </Alert>
                )}

                {/* Action Buttons */}
                <div className="d-flex gap-3 pt-3 border-top">
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      editingPlate ? handleResetPlateState() : dispatch({ type: 'toggleOpen' })
                    }
                    className="d-flex align-items-center gap-2">
                    <FaTimes /> Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={disabledSavePlateButton}
                    onClick={handleFormSubmit}
                    className="d-flex align-items-center gap-2">
                    <FaSave /> {editingPlate ? 'Save Changes' : 'Add Plate'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Collapse>
    </>
  );
}

export default NewPlateCollapse;
